import { t, useLanguage, locale } from "@/lib/i18n";
import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, UserPlus, Crown, ShieldCheck, History, UserCheck, UserX } from "lucide-react";
import { displayMemberName } from "@/lib/memberName";

export default function AccessControlModal({ open, onClose, person }) {
  useLanguage();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("Viewer");
  const [shared, setShared] = useState([]);
  const [owner, setOwner] = useState("");
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!person) return;
    try {
      const { data } = await api.get(`/persons/${person.id}/access`);
      setShared(data.shared_with || []);
      setOwner(data.owner_email || "");
      setLog(data.access_log || []);
    } catch (e) {
      /* ignore */
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line
  }, [open, person]);

  const grant = async () => {
    if (!email.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/persons/${person.id}/access`, { email, role });
      setShared(data.shared_with);
      setEmail("");
      toast.success(t("Access granted"));
      load();
    } catch (e) {
      toast.error(t(formatApiErrorDetail(e.response?.data?.detail) || t("Failed")));
    } finally {
      setLoading(false);
    }
  };

  const revoke = async (em) => {
    try {
      const { data } = await api.delete(`/persons/${person.id}/access`, { params: { email: em } });
      setShared(data.shared_with);
      toast.success(t("Access revoked"));
      load();
    } catch (e) {
      toast.error(t("Failed to revoke"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-white" data-testid="access-share-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <ShieldCheck className="text-amber-700" />{t(" Share Access")}</DialogTitle>
          <DialogDescription>{t("Control who can view {name}'s record by inviting family members via email.", { name: displayMemberName(person) })}</DialogDescription>
        </DialogHeader>

        <div className="rounded-xl bg-[#F4EFEB] border border-[#E6DEC3] p-3 flex items-center gap-2 text-sm">
          <Crown size={16} className="text-amber-700" />
          <span className="font-medium min-w-0 break-all">{owner}</span>
          <span className="ml-auto text-xs font-mono text-amber-800">{t("OWNER")}</span>
        </div>

        <div className="flex flex-wrap gap-2 items-end">
          <div className="w-full sm:w-auto sm:flex-1 min-w-0 space-y-1.5">
            <Label>{t("Invite by email")}</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="family@email.com" data-testid="share-email-input" />
          </div>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="w-32 bg-white" data-testid="share-role-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Viewer">{t("Viewer")}</SelectItem>
              <SelectItem value="Editor">{t("Editor")}</SelectItem>
              <SelectItem value="Co-Admin">{t("Co-Admin")}</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={grant} disabled={loading} className="gold-btn text-[#2C1E16] border-0" data-testid="share-grant-button">
            <UserPlus size={16} />
          </Button>
        </div>

        <div className="space-y-2 max-h-60 overflow-y-auto" data-testid="shared-list">
          {shared.length === 0 && <p className="text-sm text-stone-400 text-center py-4">{t("No one else has access yet.")}</p>}
          {shared.map((s) => (
            <div key={s.email} className="flex items-center justify-between rounded-xl border border-[#E6DEC3] px-3 py-2.5 bg-white">
              <div>
                <p className="text-sm font-medium">{s.email}</p>
                <p className="text-xs text-amber-800 font-mono">{t(s.role)}</p>
              </div>
              <button onClick={() => revoke(s.email)} className="text-red-600 hover:bg-red-50 p-2 rounded-lg" data-testid={`revoke-${s.email}`}>
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>

        {log.length > 0 && (
          <div className="pt-3 border-t border-[#E6DEC3]">
            <p className="text-xs font-mono uppercase tracking-wider text-amber-800/70 flex items-center gap-1.5 mb-2">
              <History size={13} />{t(" Access Audit Log")}</p>
            <div className="space-y-2 max-h-44 overflow-y-auto" data-testid="access-audit-log">
              {log.map((e, i) => (
                <div key={i} className="flex items-start gap-2.5 text-sm" data-testid={`audit-entry-${i}`}>
                  <div className={`mt-0.5 w-6 h-6 shrink-0 rounded-full flex items-center justify-center ${e.action === "granted" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`}>
                    {e.action === "granted" ? <UserCheck size={13} /> : <UserX size={13} />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-stone-800">
                      <span className="font-medium">{e.email}</span>{" · "}{t(e.action)}
                      {e.action === "granted" && e.role ? t(" as {0}", { 0: t(e.role) }) : ""}
                    </p>
                    <p className="text-[11px] text-stone-400 font-mono">{new Date(e.at).toLocaleString(locale())}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
